import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader1 from '../../components/common/PageHeader1';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import toast from 'react-hot-toast';
import {
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    TextField,
    Tooltip,
    InputAdornment,
    TablePagination,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
} from '@mui/material';
import userImg from "../../assets/images/user.svg";
import { fetchUsers } from '../../Redux/Reducers/UserManagementReducer';
import { useDispatch, useSelector } from 'react-redux';
import { MdOutlineInfo } from "react-icons/md";
import { BsDownload } from 'react-icons/bs';
import * as XLSX from "xlsx";
import { formatUTCDateForDataGrid, formatUTCDateForExport, formatUTCDate, convertISTToUTCForAPI } from '../../utils/dateUtils';


function UsersList() {
    const [selectedRows, setSelectedRows] = useState([]);
    const [showInactive, setShowInactive] = useState(false);
    const [inactiveUsers, setInactiveUsers] = useState([]);
    const [inactiveUsersLoading, setInactiveUsersLoading] = useState(false);
    const [inactiveRowCount, setInactiveRowCount] = useState(0);
    const [isExporting, setIsExporting] = useState(false);
    const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);

    // Wallet Balance Dialog states
    const [walletBalanceDialogOpen, setWalletBalanceDialogOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [walletBalanceData, setWalletBalanceData] = useState(null);
    const [walletTransactions, setWalletTransactions] = useState([]);
    const [walletBalanceLoading, setWalletBalanceLoading] = useState(false);
    const [walletLoadingMore, setWalletLoadingMore] = useState(false);
    const walletPageRef = useRef(1);
    const walletHasMoreRef = useRef(true);
    const walletLoadingMoreRef = useRef(false);
    const walletUserIdRef = useRef(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
    const [searchType, setSearchType] = useState('fullName'); // 'fullName', 'email', 'mobileNumber'
    const [appliedSearchType, setAppliedSearchType] = useState('fullName');

    // Helper function to get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const todayDate = getTodayDate();
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [appliedStartDate, setAppliedStartDate] = useState("");
    const [appliedEndDate, setAppliedEndDate] = useState("");
    const [lastAction, setLastAction] = useState(null); // "apply" to track when Apply Filters was clicked

    const [couponSubject, setCouponSubject] = useState('');
    const [couponMessage, setCouponMessage] = useState('');
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedUserIdToDelete, setSelectedUserIdToDelete] = useState(null);
    const [couponOpen, setCouponOpen] = useState(false);
    const [couponValue, setCouponValue] = useState('');
    const [submitAttemptedCoupon, setSubmitAttemptedCoupon] = useState(false);

    // Recharge states
    const [rechargeOpen, setRechargeOpen] = useState(false);
    const [rechargeValue, setRechargeValue] = useState('');
    const [submitAttemptedRecharge, setSubmitAttemptedRecharge] = useState(false);
    const [isSendRecharge, setIsSendRecharge] = useState(false);

    // Refund states
    const [refundOpen, setRefundOpen] = useState(false);
    const [refundOrderId, setRefundOrderId] = useState('');
    const [refundAmount, setRefundAmount] = useState('');
    const [submitAttemptedRefund, setSubmitAttemptedRefund] = useState(false);
    const [isProcessingRefund, setIsProcessingRefund] = useState(false);
    const [refundTransactions, setRefundTransactions] = useState([]);
    const [refundTransactionsLoading, setRefundTransactionsLoading] = useState(false);
    const [refundLoadingMore, setRefundLoadingMore] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const refundPageRef = useRef(1);
    const refundHasMoreRef = useRef(true);
    const refundLoadingMoreRef = useRef(false);
    const refundUserIdRef = useRef(null);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const usersFromStore = useSelector((state) => state?.UserManagementReducer?.users || []);
    const paginationInfo = useSelector((state) => state?.UserManagementReducer?.pagination);
    const loading = useSelector((state) => state?.UserManagementReducer?.loading || false);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

    const isInitialMount = useRef(true);
    const lastParamsRef = useRef(null);
    const didInitUrlSyncRef = useRef(false);
    const applySawLoadingRef = useRef(false);
    const [isApplyingFilters, setIsApplyingFilters] = useState(false);
    // Removed fileInputRef - no longer needed for CSV download

    const [isSend, setIsSend] = useState(false);
    const [isBulkUploading, setIsBulkUploading] = useState(false);
    const [isBulkExecuting, setIsBulkExecuting] = useState(false);
    const [isBulkFileUploaded, setIsBulkFileUploaded] = useState(false);
    const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
    const [selectedBulkUploadFile, setSelectedBulkUploadFile] = useState(null);
    const bulkUploadInputRef = useRef(null);

    const listLoading = showInactive ? inactiveUsersLoading : loading;

    // Restore pagination + filters from URL on first mount
    useEffect(() => {
        const pageParam = Number.parseInt(searchParams.get('page') || '0', 10);
        const pageSizeParam = Number.parseInt(searchParams.get('pageSize') || '10', 10);
        const inactiveParam = searchParams.get('inactive') === '1';
        const searchParam = searchParams.get('search') || '';
        const searchTypeParam = searchParams.get('searchType') || 'fullName';
        const startDateParam = searchParams.get('startDate') || '';
        const endDateParam = searchParams.get('endDate') || '';

        if (Number.isFinite(pageParam) && pageParam >= 0) {
            setPaginationModel((prev) => ({ ...prev, page: pageParam }));
        }
        if (Number.isFinite(pageSizeParam) && pageSizeParam > 0) {
            setPaginationModel((prev) => ({ ...prev, pageSize: pageSizeParam }));
        }

        setShowInactive(inactiveParam);

        if (searchTypeParam === 'fullName' || searchTypeParam === 'email' || searchTypeParam === 'mobileNumber') {
            setSearchType(searchTypeParam);
            setAppliedSearchType(searchTypeParam);
        }

        if (searchParam) {
            setSearchQuery(searchParam);
            setAppliedSearchQuery(searchParam);
        }
        if (startDateParam) {
            setStartDate(startDateParam);
            setAppliedStartDate(startDateParam);
        }
        if (endDateParam) {
            setEndDate(endDateParam);
            setAppliedEndDate(endDateParam);
        }

        const hasAnyUrlFilter = Boolean(searchParam) || Boolean(startDateParam) || Boolean(endDateParam);
        if (hasAnyUrlFilter) {
            setLastAction('apply');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Apply button should stay disabled until request completes.
    useEffect(() => {
        if (!isApplyingFilters) return;
        if (listLoading) {
            applySawLoadingRef.current = true;
            return;
        }
        if (!listLoading && applySawLoadingRef.current) {
            applySawLoadingRef.current = false;
            setIsApplyingFilters(false);
        }
    }, [listLoading, isApplyingFilters]);

    // Fail-safe: Ensure Apply Filters button never gets stuck
    useEffect(() => {
        let timer;
        if (isApplyingFilters) {
            timer = setTimeout(() => {
                setIsApplyingFilters(false);
                applySawLoadingRef.current = false;
            }, 500); // 0.5 seconds
        }
        return () => clearTimeout(timer);
    }, [isApplyingFilters]);

    // Keep URL in sync so Back restores pagination/filters.
    useEffect(() => {
        if (!didInitUrlSyncRef.current) {
            didInitUrlSyncRef.current = true;
            return;
        }

        const params = new URLSearchParams();
        params.set('page', String(paginationModel.page));
        params.set('pageSize', String(paginationModel.pageSize));
        if (showInactive) params.set('inactive', '1');

        if (appliedSearchQuery && appliedSearchQuery.trim()) {
            params.set('searchType', appliedSearchType);
            params.set('search', appliedSearchQuery.trim());
        }
        if (appliedStartDate) params.set('startDate', appliedStartDate);
        if (appliedEndDate) params.set('endDate', appliedEndDate);

        setSearchParams(params, { replace: true });
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        showInactive,
        appliedSearchQuery,
        appliedSearchType,
        appliedStartDate,
        appliedEndDate,
        setSearchParams,
    ]);


    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            toast.error("No file selected");
            return;
        }
        const token = localStorage.getItem("astroVani-admin-token");
        const formData = new FormData();
        formData.append("file", file);
        try {
            const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/uploadUsers`, {
                method: "POST",
                headers: { Authorization: token },
                body: formData,
            });
            const result = await response.json();
            if (response.ok) {
                toast.success("Users uploaded successfully!");
                const pageSize = paginationModel.pageSize;
                let limit = Number(pageSize);
                if (isNaN(limit)) {
                    limit = 10;
                }
                const params = {
                    page: 1,
                    limit: limit,
                };

                // Date logic: use applied dates if available, otherwise today's date for initial load
                if (appliedStartDate && appliedEndDate) {
                    params.startDate = convertISTToUTCForAPI(appliedStartDate, 'start');
                    params.endDate = convertISTToUTCForAPI(appliedEndDate, 'end');
                } else if (lastAction === null) {
                    params.startDate = convertISTToUTCForAPI(todayDate, 'start');
                    params.endDate = convertISTToUTCForAPI(todayDate, 'end');
                }

                // Add search parameters if search query exists
                if (appliedSearchQuery && appliedSearchQuery.trim()) {
                    if (searchType === 'fullName') {
                        params.fullName = appliedSearchQuery;
                    } else if (searchType === 'email') {
                        params.email = appliedSearchQuery;
                    } else if (searchType === 'mobileNumber') {
                        params.mobileNumber = appliedSearchQuery;
                    }
                }

                dispatch(fetchUsers(params));
            } else {
                toast.error(result.message || "Upload failed");
            }
        } catch (error) {
            console.error("Upload error:", error);
            toast.error("Something went wrong while uploading");
        }
    };

    const handleBulkUploadGiftAmountClick = () => {
        if (bulkUploadInputRef.current) {
            bulkUploadInputRef.current.click();
        }
    };

    const handleBulkUploadFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setSelectedBulkUploadFile(file);
        setBulkUploadModalOpen(true);

        if (event.target) event.target.value = null; // Reset input
    };

    const handleConfirmBulkUpload = async () => {
        if (!selectedBulkUploadFile) return;

        setIsBulkUploading(true);
        const token = localStorage.getItem("astroVani-admin-token");
        const formData = new FormData();
        formData.append("file", selectedBulkUploadFile);

        try {
            const response = await axios.post(`${process.env.REACT_APP_BASEURL}/admin/BulkUploadGiftAmount`, formData, {
                headers: {
                    'Authorization': token,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.status === 200 || response.status === 201) {
                toast.success("CSV Uploaded successfully!");
                setIsBulkFileUploaded(true);
                setBulkUploadModalOpen(false);
                setSelectedBulkUploadFile(null);
            } else {
                toast.error(response.data?.message || "Upload failed");
                setIsBulkFileUploaded(false);
            }
        } catch (error) {
            console.error("Bulk Upload Error:", error);
            toast.error(error.response?.data?.message || "Something went wrong while uploading CSV");
        } finally {
            setIsBulkUploading(false);
        }
    };

    const handleCancelBulkUpload = () => {
        setBulkUploadModalOpen(false);
        setSelectedBulkUploadFile(null);
    };

    const handleExecuteBulkGiftAmount = async () => {
        setIsBulkExecuting(true);
        const token = localStorage.getItem("astroVani-admin-token");

        try {
            const response = await axios.post(`${process.env.REACT_APP_BASEURL}/admin/executeBulkGiftAmount`, {}, {
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 200 || response.status === 201) {
                toast.success("Bulk GiftAmount Executed successfully!");
                setIsBulkFileUploaded(false);
                // Optionally refresh user list if needed
                handleApplyFilters();
            } else {
                toast.error(response.data?.message || "Execution failed");
            }
        } catch (error) {
            console.error("Bulk Execution Error:", error);
            toast.error(error.response?.data?.message || "Something went wrong while executing bulk GiftAmount");
        } finally {
            setIsBulkExecuting(false);
        }
    };
    const handleDownloadCSVFormat = () => {
        const sampleData = [
            ["Full Name", "Email", "Mobile Number", "Country Code", "State", "City", "Address", "DOB", "Time of Birth"],
            ["User-1", "user1@example.com", "98XXXXXXX1", "91", "Delhi", "New Delhi", "123 Main Street", "15-05-1990", "10:30 AM"],
            ["User-2", "user2@example.com", "98XXXXXXX2", "91", "Delhi", "New Delhi", "123 Main Street", "15-05-2000", "02:30 PM"],
            ["User-1", "user1@example.com", "98XXXXXXX3", "91", "Delhi", "New Delhi", "123 Main Street", "15-05-2014", "08:00 AM"],
        ];

        const csvContent = sampleData.map((row) => row.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "user_upload_format.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadCSV = async () => {
        const token = localStorage.getItem("astroVani-admin-token");
        setIsDownloadingCSV(true);

        try {
            const params = new URLSearchParams();

            // Send dates - use today's date if not provided
            const exportStartDate = appliedStartDate || todayDate;
            const exportEndDate = appliedEndDate || todayDate;
            params.append('startDate', convertISTToUTCForAPI(exportStartDate, 'start'));
            params.append('endDate', convertISTToUTCForAPI(exportEndDate, 'end'));

            // Include search parameters in export if search was applied
            if (appliedSearchQuery && appliedSearchQuery.trim()) {
                if (appliedSearchType === 'fullName') {
                    params.append('fullName', appliedSearchQuery);
                } else if (appliedSearchType === 'email') {
                    params.append('email', appliedSearchQuery);
                } else if (appliedSearchType === 'mobileNumber') {
                    params.append('mobileNumber', appliedSearchQuery);
                }
            }

            const response = await fetch(
                `${process.env.REACT_APP_BASEURL}/admin/user/export?${params.toString()}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: token,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch user data");
            }

            const result = await response.json();
            const users = Array.isArray(result) ? result : result?.data || [];
            if (!users.length) {
                toast.error("No user data available to download");
                return;
            }

            // Fields: fullName, email, mobileNumber, createdAt, updatedAt (split into date and time)
            const headers = ["Full Name", "Email", "Mobile Number", "Created Date", "Created Time", "Updated Date", "Updated Time"];

            // Create CSV content
            const csvRows = [
                headers.join(","), // Header row
                ...users.map((user) => [
                    `"${(user.fullName || "").replace(/"/g, '""')}"`, // Escape quotes
                    `"${(user.email || "").replace(/"/g, '""')}"`,
                    `"${(user.mobileNumber || "").replace(/"/g, '""')}"`,
                    `"${formatUTCDate(user.createdAt, { format: 'date', separator: '/' }).replace(/"/g, '""')}"`,
                    `"${formatUTCDate(user.createdAt, { format: 'time', showSeconds: true }).replace(/"/g, '""')}"`,
                    `"${formatUTCDate(user.updatedAt, { format: 'date', separator: '/' }).replace(/"/g, '""')}"`,
                    `"${formatUTCDate(user.updatedAt, { format: 'time', showSeconds: true }).replace(/"/g, '""')}"`,
                ].join(",")),
            ];

            const csvContent = csvRows.join("\n");
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "users_export.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            toast.success("CSV downloaded successfully!");
        } catch (error) {
            console.error("CSV download failed:", error);
            toast.error("Failed to download CSV");
        } finally {
            setIsDownloadingCSV(false);
        }
    };

    const fetchInactiveUsers = useCallback(async (page, limit, search = '') => {
        setInactiveUsersLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/inactiveUsers`, {
                headers: { 'Authorization': `${localStorage.getItem("astroVani-admin-token")}` },
                params: {
                    page,
                    limit,
                    search,
                }
            });

            if (response.status === 200 && response.data.data && typeof response.data.data !== 'undefined') {
                setInactiveUsers(response.data.data || []);
                setInactiveRowCount(response.data.paginationDetail?.totalDocs || 0);
            } else {
                setInactiveUsers([]);
                setInactiveRowCount(0);
            }
        } catch (error) {
            console.error('Error fetching inactive users:', error);
            setInactiveUsers([]);
            setInactiveRowCount(0);
            toast.error(error.response?.data?.message || 'Failed to fetch inactive users.');
        } finally {
            setInactiveUsersLoading(false);
        }
    }, []);

    // Apply Filters Handler - Applies search and date filters together
    const handleApplyFilters = () => {
        setIsApplyingFilters(true);
        applySawLoadingRef.current = false;
        setAppliedSearchQuery(searchQuery.trim());
        setAppliedSearchType(searchType);
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
        setLastAction("apply");
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    // Reset Filter Handler
    const handleResetFilter = () => {
        setStartDate("");
        setEndDate("");
        setAppliedStartDate("");
        setAppliedEndDate("");
        setSearchQuery("");
        setAppliedSearchQuery("");
        setSearchType('fullName');
        setAppliedSearchType('fullName');
        setLastAction(null);
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    useEffect(() => {
        const { page, pageSize } = paginationModel;
        let limit = Number(pageSize);
        if (isNaN(limit)) {
            limit = 10000; // Use fixed default
        }

        const params = {
            page: page + 1,
            limit: limit,
        };

        // Date logic:
        // 1. If dates are applied (from Apply Filter), use them
        // 2. If no dates applied and it's initial load (lastAction is null), use today's date
        // 3. If no dates applied and Apply Filter was clicked (lastAction is "apply"), don't send dates
        if (appliedStartDate && appliedEndDate) {
            // Dates were explicitly applied - use them
            params.startDate = convertISTToUTCForAPI(appliedStartDate, 'start');
            params.endDate = convertISTToUTCForAPI(appliedEndDate, 'end');
        } else if (lastAction === null) {
            // Initial load or pagination change with no dates - send today's date by default
            params.startDate = convertISTToUTCForAPI(todayDate, 'start');
            params.endDate = convertISTToUTCForAPI(todayDate, 'end');
        }
        // If lastAction is "apply" and no dates, don't send dates (params won't have startDate/endDate)

        // Add search parameters if search query exists
        if (appliedSearchQuery && appliedSearchQuery.trim()) {
            if (searchType === 'fullName') {
                params.fullName = appliedSearchQuery;
            } else if (searchType === 'email') {
                params.email = appliedSearchQuery;
            } else if (searchType === 'mobileNumber') {
                params.mobileNumber = appliedSearchQuery;
            }
        }

        // Prevent duplicate API calls by comparing params
        const paramsString = JSON.stringify(params);
        if (lastParamsRef.current === paramsString && !isInitialMount.current) {
            return; // Skip if params haven't changed
        }

        lastParamsRef.current = paramsString;
        isInitialMount.current = false;

        // Dispatch with combined params
        if (showInactive) {
            fetchInactiveUsers(page + 1, limit, appliedSearchQuery);
        } else {
            dispatch(fetchUsers(params));
        }
    }, [paginationModel, dispatch, showInactive, appliedSearchQuery, appliedStartDate, appliedEndDate, searchType, lastAction, todayDate, fetchInactiveUsers]);


    const handleManageSelected = () => {
        setSearchQuery('');
        setAppliedSearchQuery('');
        setStartDate("");
        setEndDate("");
        setAppliedStartDate("");
        setAppliedEndDate("");
        setSearchType('fullName');
        setLastAction(null);
        setShowInactive(true);
        setSelectedRows([]);
        setPaginationModel(prev => ({ page: 0, pageSize: prev.pageSize }));
    };

    const handleShowAllUsers = () => {
        setSearchQuery('');
        setAppliedSearchQuery('');
        setStartDate("");
        setEndDate("");
        setAppliedStartDate("");
        setAppliedEndDate("");
        setSearchType('fullName');
        setLastAction(null);
        setShowInactive(false);
        setSelectedRows([]);
        setPaginationModel(prev => ({ page: 0, pageSize: prev.pageSize }));
    };

    const handleChangePage = (event, newPage) => {
        setPaginationModel({ ...paginationModel, page: newPage });
    };

    const handleChangeRowsPerPage = (event) => {
        const value = event.target.value;
        const newPageSize = parseInt(value, 10);
        setPaginationModel({ page: 0, pageSize: newPageSize });
    };

    const handleCouponClick = () => {
        if (selectedRows.length === 0) {
            toast.error("Please select users to send a coupon.");
            return;
        }
        setCouponOpen(true);
    };

    const handleRechargeClick = () => {
        if (selectedRows.length !== 1) {
            toast.error("Please select exactly one user for recharge.");
            return;
        }
        setRechargeOpen(true);
    };

    const handleRefundClick = async () => {
        if (selectedRows.length !== 1) {
            toast.error("Please select exactly one user for refund.");
            return;
        }
        const userId = selectedRows[0];
        refundUserIdRef.current = userId;
        refundPageRef.current = 1;
        refundHasMoreRef.current = true;
        refundLoadingMoreRef.current = false;
        setRefundLoadingMore(false);

        setRefundOpen(true);
        setRefundTransactionsLoading(true);
        setRefundTransactions([]);
        setSelectedTransaction(null);
        setRefundOrderId('');
        setRefundAmount('');

        try {
            const token = localStorage.getItem("astroVani-admin-token");
            const response = await axios.get(
                `${process.env.REACT_APP_BASEURL}/admin/walletBalance/${userId}?type=debit&page=1&limit=15`,
                {
                    headers: { Authorization: token }
                }
            );

            if (response.status === 200 && response.data?.success) {
                const data = response.data?.data || {};
                const transactions = Array.isArray(data.transactions) ? data.transactions : [];
                setRefundTransactions(transactions);

                const pagination = data.pagination || response.data?.pagination || response.data?.paginationDetail || data.paginationDetail;
                const hasMore = (pagination && typeof pagination.totalPages === 'number')
                    ? 1 < pagination.totalPages
                    : (pagination && typeof pagination.total === 'number')
                        ? transactions.length < pagination.total
                        : transactions.length === 15;
                refundHasMoreRef.current = hasMore;
            } else {
                toast.error(response.data?.message || "Failed to fetch wallet transactions");
                setRefundTransactions([]);
                refundHasMoreRef.current = false;
            }
        } catch (error) {
            console.error("Error fetching wallet transactions:", error);
            toast.error(error.response?.data?.message || "Failed to fetch wallet transactions");
            setRefundTransactions([]);
            refundHasMoreRef.current = false;
        } finally {
            setRefundTransactionsLoading(false);
        }
    };

    const loadMoreRefundTransactions = async () => {
        if (refundLoadingMoreRef.current || !refundHasMoreRef.current || !refundUserIdRef.current) {
            return;
        }
        refundLoadingMoreRef.current = true;
        setRefundLoadingMore(true);

        const nextPage = refundPageRef.current + 1;
        try {
            const token = localStorage.getItem("astroVani-admin-token");
            const response = await axios.get(
                `${process.env.REACT_APP_BASEURL}/admin/walletBalance/${refundUserIdRef.current}?type=debit&page=${nextPage}&limit=15`,
                {
                    headers: { Authorization: token }
                }
            );

            if (response.status === 200 && response.data?.success) {
                const data = response.data?.data || {};
                const newTransactions = Array.isArray(data.transactions) ? data.transactions : [];

                setRefundTransactions((prev) => {
                    const existingIds = new Set(prev.map(t => t._id || t.orderId || t.transactionId).filter(Boolean));
                    const filtered = newTransactions.filter(t => {
                        const id = t._id || t.orderId || t.transactionId;
                        return !id || !existingIds.has(id);
                    });
                    return [...prev, ...filtered];
                });

                refundPageRef.current = nextPage;

                const pagination = data.pagination || response.data?.pagination || response.data?.paginationDetail || data.paginationDetail;
                const hasMore = (pagination && typeof pagination.totalPages === 'number')
                    ? nextPage < pagination.totalPages
                    : (pagination && typeof pagination.total === 'number')
                        ? (refundPageRef.current * 15) < pagination.total
                        : newTransactions.length === 15;
                refundHasMoreRef.current = hasMore;
            } else {
                refundHasMoreRef.current = false;
            }
        } catch (error) {
            console.error("Error loading more refund transactions:", error);
            refundHasMoreRef.current = false;
        } finally {
            refundLoadingMoreRef.current = false;
            setRefundLoadingMore(false);
        }
    };

    const handleRefundScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 50) {
            loadMoreRefundTransactions();
        }
    };

    const handleRefundClose = () => {
        setRefundOpen(false);
        setRefundOrderId('');
        setRefundAmount('');
        setSubmitAttemptedRefund(false);
        setIsProcessingRefund(false);
        setRefundTransactions([]);
        setSelectedTransaction(null);
        refundPageRef.current = 1;
        refundHasMoreRef.current = true;
        refundLoadingMoreRef.current = false;
        refundUserIdRef.current = null;
        setRefundLoadingMore(false);
    };

    const handleTransactionSelect = (transaction) => {
        setSelectedTransaction(transaction);
        setRefundOrderId(transaction.orderId || '');
        setRefundAmount(transaction.amount ? Math.abs(transaction.amount).toString() : '');
    };

    const handleRefundSubmit = async () => {
        setSubmitAttemptedRefund(true);

        // Validate fields
        if (!refundOrderId.trim()) {
            toast.error("Please enter or select an Order ID.");
            return;
        }

        const amount = parseFloat(refundAmount);
        if (isNaN(amount) || amount < 1 || amount > 9999999) {
            toast.error("Refund amount must be between ₹1 and ₹9,999,999.");
            return;
        }

        // Validate refund amount doesn't exceed transaction amount
        if (selectedTransaction?.amount && amount > Math.abs(selectedTransaction.amount)) {
            toast.error(`Refund amount cannot exceed transaction amount (₹${Math.abs(selectedTransaction.amount)}).`);
            return;
        }

        if (selectedRows.length !== 1) {
            toast.error("Please select exactly one user.");
            return;
        }

        setIsProcessingRefund(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BASEURL}/admin/refund`,
                {
                    userId: selectedRows[0],
                    orderId: refundOrderId.trim(),
                    refundAmount: amount,
                },
                {
                    headers: {
                        Authorization: `${localStorage.getItem("astroVani-admin-token")}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                toast.success("Refund processed successfully!");
                handleRefundClose();
                setSelectedRows([]);
                const { page, pageSize } = paginationModel;
                let limit = Number(pageSize);
                if (isNaN(limit)) {
                    limit = 10;
                }
                const params = {
                    page: page + 1,
                    limit: limit,
                };

                // Date logic: use applied dates if available, otherwise today's date for initial load
                if (appliedStartDate && appliedEndDate) {
                    params.startDate = convertISTToUTCForAPI(appliedStartDate, 'start');
                    params.endDate = convertISTToUTCForAPI(appliedEndDate, 'end');
                } else if (lastAction === null) {
                    params.startDate = convertISTToUTCForAPI(todayDate, 'start');
                    params.endDate = convertISTToUTCForAPI(todayDate, 'end');
                }

                // Add search parameters if search query exists
                if (appliedSearchQuery && appliedSearchQuery.trim()) {
                    if (searchType === 'fullName') {
                        params.fullName = appliedSearchQuery;
                    } else if (searchType === 'email') {
                        params.email = appliedSearchQuery;
                    } else if (searchType === 'mobileNumber') {
                        params.mobileNumber = appliedSearchQuery;
                    }
                }

                if (showInactive) {
                    fetchInactiveUsers(page + 1, limit, appliedSearchQuery);
                } else {
                    dispatch(fetchUsers(params));
                }
            } else {
                toast.error(response.data?.message || "Failed to process refund.");
            }
        } catch (error) {
            console.error("Error processing refund:", error);
            toast.error(
                error.response?.data?.message ||
                "Failed to process refund. Please try again."
            );
        } finally {
            setIsProcessingRefund(false);
        }
    };

    const handleRechargeClose = () => {
        setRechargeOpen(false);
        setRechargeValue('');
        setSubmitAttemptedRecharge(false);
        setIsSendRecharge(false);
    };

    const handleRechargeSend = async () => {
        setSubmitAttemptedRecharge(true);

        // ✅ Validate recharge amount
        const amount = parseFloat(rechargeValue);
        if (isNaN(amount) || amount < 1 || amount > 9999999) {
            toast.error("Recharge amount must be between ₹1 and ₹9,999,999.");
            return;
        }

        // ✅ Proceed with recharge
        setIsSendRecharge(true);
        try {
            const selectedUser = baseDataSet.find(user => user._id === selectedRows[0]);
            const response = await axios.post(
                `${process.env.REACT_APP_BASEURL}/admin/successRecharge`,
                {
                    amount: amount,
                    userId: [selectedRows[0]],
                    // name: selectedUser?.fullName,
                },
                {
                    headers: {
                        Authorization: `${localStorage.getItem("astroVani-admin-token")}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                toast.success("Recharge successful!");
                handleRechargeClose();
                setSelectedRows([]);
                const { page, pageSize } = paginationModel;
                let limit = Number(pageSize);
                if (isNaN(limit)) {
                    limit = 10;
                }
                const params = {
                    page: page + 1,
                    limit: limit,
                };

                // Date logic: use applied dates if available, otherwise today's date for initial load
                if (appliedStartDate && appliedEndDate) {
                    params.startDate = convertISTToUTCForAPI(appliedStartDate, 'start');
                    params.endDate = convertISTToUTCForAPI(appliedEndDate, 'end');
                } else if (lastAction === null) {
                    params.startDate = convertISTToUTCForAPI(todayDate, 'start');
                    params.endDate = convertISTToUTCForAPI(todayDate, 'end');
                }

                // Add search parameters if search query exists
                if (appliedSearchQuery && appliedSearchQuery.trim()) {
                    if (searchType === 'fullName') {
                        params.fullName = appliedSearchQuery;
                    } else if (searchType === 'email') {
                        params.email = appliedSearchQuery;
                    } else if (searchType === 'mobileNumber') {
                        params.mobileNumber = appliedSearchQuery;
                    }
                }

                if (showInactive) {
                    fetchInactiveUsers(page + 1, limit, appliedSearchQuery);
                } else {
                    dispatch(fetchUsers(params));
                }
            } else {
                toast.error(response.data?.message || "Failed to process recharge.");
            }
        } catch (error) {
            console.error("Error processing recharge:", error);
            toast.error(
                error.response?.data?.message ||
                "Failed to process recharge. Please try again."
            );
        } finally {
            setIsSendRecharge(false);
        }
    };

    const handleCouponClose = () => {
        setCouponOpen(false);
        setCouponValue('');
        setCouponSubject('');
        setCouponMessage('');
        setSubmitAttemptedCoupon(false);
        setIsSend(false);
    };

    const handleCouponSend = async () => {
        setSubmitAttemptedCoupon(true);

        // ✅ Validate coupon value (robust to string or number input)
        const amount = parseFloat(couponValue);
        if (isNaN(amount) || amount < 1 || amount > 9999999) {
            toast.error("Coupon amount must be between ₹1 and ₹9,999,999.");
            return;
        }

        // ✅ Validate other fields
        if (selectedRows.length === 0) {
            toast.error("Please select at least one user.");
            return;
        }

        if (!couponSubject.trim()) {
            toast.error("Please enter a subject.");
            return;
        }

        if (!couponMessage.trim()) {
            toast.error("Please enter a message.");
            return;
        }

        // ✅ Proceed with sending
        setIsSend(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BASEURL}/admin/userGiftAmount`,
                {
                    amount: amount,
                    subject: couponSubject,
                    message: couponMessage,
                    userIds: selectedRows,
                },
                {
                    headers: {
                        Authorization: `${localStorage.getItem("astroVani-admin-token")}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                toast.success("Coupon sent successfully!");
                handleCouponClose();
                setSelectedRows([]);
                setCouponValue("");
                setCouponSubject("");
                setCouponMessage("");
            } else {
                toast.error(response.data?.message || "Failed to send coupon.");
            }
        } catch (error) {
            console.error("Error sending coupon:", error);
            toast.error(
                error.response?.data?.message ||
                "Failed to send the coupon. Please try again."
            );
        } finally {
            setIsSend(false);
        }
    };

    const handleDeleteUserClick = (userId) => {
        setSelectedUserIdToDelete(userId);
        setOpenDeleteDialog(true);
    };

    const handleDeleteDialogClose = () => {
        setOpenDeleteDialog(false);
        setSelectedUserIdToDelete(null);
    };

    const getActiveQueryParams = useCallback((page, pageSize) => {
        const params = {
            page: (page ?? paginationModel.page) + 1,
            limit: pageSize ?? paginationModel.pageSize,
        };
        if (appliedStartDate && appliedEndDate) {
            params.startDate = convertISTToUTCForAPI(appliedStartDate, 'start');
            params.endDate = convertISTToUTCForAPI(appliedEndDate, 'end');
        } else if (lastAction === null) {
            params.startDate = convertISTToUTCForAPI(todayDate, 'start');
            params.endDate = convertISTToUTCForAPI(todayDate, 'end');
        }
        if (appliedSearchQuery?.trim()) {
            params[appliedSearchType] = appliedSearchQuery.trim();
        }
        return params;
    }, [paginationModel, appliedStartDate, appliedEndDate, appliedSearchQuery, appliedSearchType, lastAction, todayDate]);

    const handleDeleteConfirm = async () => {
        if (!selectedUserIdToDelete) return;
        try {
            const response = await axios.delete(`${process.env.REACT_APP_BASEURL}/admin/user/${selectedUserIdToDelete}`, {
                headers: { 'Authorization': ` ${localStorage.getItem("astroVani-admin-token")}` },
            });
            if (response.status === 200) {
                toast.success("User deleted successfully!");
                setSelectedRows(prev => prev.filter(id => id !== selectedUserIdToDelete));

                // YAHAN FILTERS MAINTAIN HO RAHE HAIN
                const params = getActiveQueryParams(); // Yeh current filters uthayega
                if (showInactive) {
                    fetchInactiveUsers(params.page, params.limit, appliedSearchQuery);
                } else {
                    dispatch(fetchUsers(params)); // Wahi search/date params wapas bhejega
                }
            } else {
                toast.error(response.data?.message || "Failed to delete user.");
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error(error.response?.data?.message || "Failed to delete the user.");
        } finally {
            handleDeleteDialogClose();
        }
    };

    const handleServiceEnabledToggle = async (rowId, currentStatus) => {
        const token = localStorage.getItem("astroVani-admin-token");
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BASEURL}/admin/user/toggleStatus/${rowId}`,
                {
                    method: "PATCH",
                    headers: { Authorization: token, "Content-Type": "application/json" },
                    body: JSON.stringify({ isActive: !currentStatus }),
                }
            );
            const result = await response.json();
            if (response.ok && result.success) {
                const newStatus = !currentStatus;
                toast.success(newStatus ? "Services disabled successfully" : "Services enabled successfully");
                handleResetFilter();
            } else {
                toast.error(result.message || "Failed to update service status");
            }
        } catch (err) {
            console.error("Toggle error:", err);
            toast.error("Failed to update service status");
        }
    };

    // Handle Bookings (B) icon click - redirect to booking list
    const handleBookingsClick = (mobileNumber) => {
        window.open(`/booking-list?redirect=${mobileNumber}`, '_blank');
    };

    // Handle Session Report (S) icon click - redirect to session reports
    const handleSessionReportClick = (mobileNumber) => {
        window.open(`/session-reports?redirect=${mobileNumber}`, '_blank');
    };

    // Handle Wallet Balance (W) icon click - show wallet balance in popup
    const handleWalletBalanceClick = async (userId) => {
        setSelectedUserId(userId);
        walletUserIdRef.current = userId;
        walletPageRef.current = 1;
        walletHasMoreRef.current = true;
        walletLoadingMoreRef.current = false;
        setWalletLoadingMore(false);

        setWalletBalanceDialogOpen(true);
        setWalletBalanceLoading(true);
        setWalletBalanceData(null);
        setWalletTransactions([]);

        try {
            const token = localStorage.getItem("astroVani-admin-token");
            const response = await axios.get(
                `${process.env.REACT_APP_BASEURL}/admin/walletBalance/${userId}?page=1&limit=15`,
                {
                    headers: { Authorization: token }
                }
            );

            if (response.status === 200 && response.data?.success) {
                const data = response.data?.data || {};
                setWalletBalanceData(data);
                const transactions = Array.isArray(data.transactions) ? data.transactions : [];
                setWalletTransactions(transactions);

                const pagination = data.pagination || response.data?.pagination || response.data?.paginationDetail || data.paginationDetail;
                const hasMore = (pagination && typeof pagination.totalPages === 'number')
                    ? 1 < pagination.totalPages
                    : (pagination && typeof pagination.total === 'number')
                        ? transactions.length < pagination.total
                        : transactions.length === 15;
                walletHasMoreRef.current = hasMore;
            } else {
                toast.error(response.data?.message || "Failed to fetch wallet balance data");
                setWalletBalanceData(null);
                setWalletTransactions([]);
                walletHasMoreRef.current = false;
            }
        } catch (error) {
            console.error("Error fetching wallet balance:", error);
            toast.error(error.response?.data?.message || "Failed to fetch wallet balance data");
            setWalletBalanceData(null);
            setWalletTransactions([]);
            walletHasMoreRef.current = false;
        } finally {
            setWalletBalanceLoading(false);
        }
    };

    const loadMoreWalletTransactions = async () => {
        if (walletLoadingMoreRef.current || !walletHasMoreRef.current || !walletUserIdRef.current) {
            return;
        }
        walletLoadingMoreRef.current = true;
        setWalletLoadingMore(true);

        const nextPage = walletPageRef.current + 1;
        try {
            const token = localStorage.getItem("astroVani-admin-token");
            const response = await axios.get(
                `${process.env.REACT_APP_BASEURL}/admin/walletBalance/${walletUserIdRef.current}?page=${nextPage}&limit=15`,
                {
                    headers: { Authorization: token }
                }
            );

            if (response.status === 200 && response.data?.success) {
                const data = response.data?.data || {};
                const newTransactions = Array.isArray(data.transactions) ? data.transactions : [];

                setWalletTransactions((prev) => {
                    const existingIds = new Set(prev.map(t => t._id || t.transactionId || t.orderId).filter(Boolean));
                    const filtered = newTransactions.filter(t => {
                        const id = t._id || t.transactionId || t.orderId;
                        return !id || !existingIds.has(id);
                    });
                    return [...prev, ...filtered];
                });

                walletPageRef.current = nextPage;

                const pagination = data.pagination || response.data?.pagination || response.data?.paginationDetail || data.paginationDetail;
                const hasMore = (pagination && typeof pagination.totalPages === 'number')
                    ? nextPage < pagination.totalPages
                    : (pagination && typeof pagination.total === 'number')
                        ? (walletPageRef.current * 15) < pagination.total
                        : newTransactions.length === 15;
                walletHasMoreRef.current = hasMore;
            } else {
                walletHasMoreRef.current = false;
            }
        } catch (error) {
            console.error("Error loading more wallet transactions:", error);
            walletHasMoreRef.current = false;
        } finally {
            walletLoadingMoreRef.current = false;
            setWalletLoadingMore(false);
        }
    };

    const handleWalletScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 50) {
            loadMoreWalletTransactions();
        }
    };

    const handleWalletBalanceClose = () => {
        setWalletBalanceDialogOpen(false);
        setSelectedUserId(null);
        setWalletBalanceData(null);
        setWalletTransactions([]);
        walletPageRef.current = 1;
        walletHasMoreRef.current = true;
        walletLoadingMoreRef.current = false;
        walletUserIdRef.current = null;
        setWalletLoadingMore(false);
    };

    const columns = [
        { field: 'sono', headerName: 'S.No', width: 70, sortable: false, filterable: false },
        {
            field: "actions", headerName: "Actions", width: 200, sortable: false, filterable: false,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <IconButton component={Link} to={`/users-edit/${row._id}`} color="primary" size="small">
                        <i className="icofont-edit text-danger" style={{ fontSize: '1.2rem' }}></i>
                    </IconButton>
                    <Tooltip title="Bookings">
                        <IconButton
                            onClick={() => handleBookingsClick(row.mobileNumber)}
                            size="small"
                            sx={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}
                        >
                            B
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Session Report">
                        <IconButton
                            onClick={() => handleSessionReportClick(row.mobileNumber)}
                            size="small"
                            sx={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}
                        >
                            S
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Wallet Balance">
                        <IconButton
                            onClick={() => handleWalletBalanceClick(row._id)}
                            size="small"
                            sx={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}
                        >
                            W
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
        // {
        //     field: 'profileImg', headerName: 'Image', width: 80,
        //     renderCell: (params) => (<img src={params.value || userImg} alt="User" style={{ width: '30px', height: '30px', borderRadius: '50%', objectFit: 'cover' }} />),
        //     sortable: false, filterable: false,
        // },
        {
            field: 'fullName', headerName: 'Name', width: 200,
            renderCell: (params) => (
                <Button variant="text" size="small" sx={{ textTransform: 'none', p: 0, justifyContent: 'flex-start' }} onClick={() => navigate(`/booking-by-user/${params.row._id}`)}>
                    {params.value}
                </Button>
            ),
        },
        { field: 'email', headerName: 'Email', width: 250 },
        { field: 'mobileNumber', headerName: 'Mobile', width: 130 },
        { field: 'createdAt', headerName: 'Created At', width: 170 },
        { field: 'updatedAt', headerName: 'Updated At', width: 170 },
        {
            field: 'isActive',
            headerName: 'Disable Service',
            width: 150,
            renderCell: (params) => {
                return (
                    <Switch
                        checked={params.row.isActive}
                        onChange={() => handleServiceEnabledToggle(params.row.id, params.row.isActive)}
                        color="primary"
                    />
                )
            },
        }, {
            field: "delete", headerName: "Delete", width: 0, sortable: false, filterable: false,
            renderCell: ({ row }) => (
                <Box>
                    <IconButton color="error" onClick={() => handleDeleteUserClick(row._id)}>
                        <i className="icofont-ui-delete text-danger" style={{ fontSize: '1.2rem' }}></i>
                    </IconButton>
                </Box>
            ),
        },
    ];

    const baseDataSet = showInactive ? inactiveUsers : usersFromStore;

    const currentGridRows = (baseDataSet || []).map((user, index) => {
        const pageSize = paginationModel.pageSize;
        const serialNumberBase = paginationModel.page * pageSize;
        return {
            id: user._id,
            _id: user._id,
            sono: serialNumberBase + index + 1,
            profileImg: user.profileImg || userImg,
            fullName: user.fullName || 'N/A',
            email: user.email || '-',
            mobileNumber: user.mobileNumber || '-',
            isActive: user.isActive || false,
            createdAt: user.createdAt ? new Date(user.createdAt).toLocaleString() : "",
            updatedAt: user.updatedAt ? new Date(user.updatedAt).toLocaleString() : "",
        };
    });

    const handleExport = async () => {
        const token = localStorage.getItem("astroVani-admin-token");
        setIsExporting(true);

        try {
            const params = new URLSearchParams();

            // Send dates - use today's date if not provided
            const exportStartDate = appliedStartDate || todayDate;
            const exportEndDate = appliedEndDate || todayDate;
            params.append('startDate', convertISTToUTCForAPI(exportStartDate, 'start'));
            params.append('endDate', convertISTToUTCForAPI(exportEndDate, 'end'));

            // Include search parameters in export if search was applied
            if (appliedSearchQuery && appliedSearchQuery.trim()) {
                if (appliedSearchType === 'fullName') {
                    params.append('fullName', appliedSearchQuery);
                } else if (appliedSearchType === 'email') {
                    params.append('email', appliedSearchQuery);
                } else if (appliedSearchType === 'mobileNumber') {
                    params.append('mobileNumber', appliedSearchQuery);
                }
            }

            const response = await fetch(
                `${process.env.REACT_APP_BASEURL}/admin/user/export?${params.toString()}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: token,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to export user data");
            }

            const result = await response.json();
            const users = Array.isArray(result) ? result : result?.data || [];
            if (!users.length) {
                toast.error("No user data available to export");
                return;
            }

            // Export fields: fullName, email, mobileNumber, createdAt, updatedAt (split into date and time)
            const headers = [
                "Full Name",
                "Email",
                "Mobile Number",
                "Created Date",
                "Created Time",
                "Updated Date",
                "Updated Time",
                "Disable Service",
            ];

            const rows = users.map((user) => [
                user.fullName || "",
                user.email || "",
                user.mobileNumber || "",
                formatUTCDate(user.createdAt, { format: 'date', separator: '/' }),
                formatUTCDate(user.createdAt, { format: 'time', showSeconds: true }),
                formatUTCDate(user.updatedAt, { format: 'date', separator: '/' }),
                formatUTCDate(user.updatedAt, { format: 'time', showSeconds: true }),
                user.isActive ? "Enabled" : "Disabled",
            ]);

            const worksheetData = [headers, ...rows];
            const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

            // Auto column width
            const columnWidths = headers.map((header) => ({ wch: header.length + 10 }));
            worksheet["!cols"] = columnWidths;

            // Save Excel file
            XLSX.writeFile(workbook, "users_export.xlsx");

            toast.success("Users exported successfully!");
        } catch (error) {
            console.error("Export failed:", error);
            toast.error("Failed to export users");
        } finally {
            setIsExporting(false);
        }
    };
    const handleDownloadSampleGiftAmountCSV = () => {
        const csvContent = "mobileNumber,giftAmount\n86XXXXXXX5,50\n77XXXXXXX9,30\n70XXXXXXX0,20\n91XXXXXXX8,10";
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "Sample_GiftAmount.csv");
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <>
            <div className="body d-flex">
                <div className="container-xxl">
                    <PageHeader1 righttitle="Add Users" link="/users-add" routebutton={true} pagetitle={<span className="page-header-spacing">Users List ({showInactive ? inactiveRowCount : (paginationInfo?.totalDocs || 0)})</span>} />
                    <div className="row g-0 mb-3 mt-1">
                        <div className="col-md-12">
                            <div className="card modern-card">
                                <div className="card-body p-3">
                                    <Box sx={{ width: '100%' }}>
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            gap={2}
                                            mb={2}
                                            className="modern-header w-100"
                                        >
                                            {/* Top Line: Search + Date Filters */}
                                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                <Tooltip title="Download CSV Format">
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleDownloadCSVFormat}
                                                        sx={{
                                                            color: 'var(--color-500)',
                                                            flexShrink: 0
                                                        }}
                                                    >
                                                        <MdOutlineInfo />
                                                    </IconButton>
                                                </Tooltip>
                                                <FormControl size="small" sx={{ minWidth: 120, flexShrink: 0 }}>
                                                    <InputLabel>Search By</InputLabel>
                                                    <Select
                                                        value={searchType}
                                                        label="Search By"
                                                        onChange={(e) => setSearchType(e.target.value)}
                                                        sx={{ borderRadius: '8px' }}
                                                    >
                                                        <MenuItem value="fullName">Full Name</MenuItem>
                                                        <MenuItem value="email">Email</MenuItem>
                                                        <MenuItem value="mobileNumber">Mobile Number</MenuItem>
                                                    </Select>
                                                </FormControl>
                                                <TextField
                                                    placeholder={
                                                        searchType === "fullName" ? "Search by full name..." :
                                                            searchType === "email" ? "Search by email..." :
                                                                "Search by mobile number..."
                                                    }
                                                    variant="outlined"
                                                    size="small"
                                                    className="modern-search"
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    onKeyPress={(e) => { if (e.key === 'Enter') { handleApplyFilters(); } }}
                                                    sx={{
                                                        width: { xs: '150px', sm: '180px', md: '200px' },
                                                        flexShrink: 0,
                                                        background: 'var(--card-color)',
                                                        borderRadius: '9px',
                                                        '& .MuiInputBase-input': { fontSize: '13px', padding: '9px 10px 9px 0' },
                                                    }}
                                                />
                                                <TextField
                                                    label="Start Date"
                                                    type="date"
                                                    variant="outlined"
                                                    size="small"
                                                    value={startDate || ""}
                                                    onChange={(e) => setStartDate(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{
                                                        width: { xs: '140px', sm: '160px', md: '180px' },
                                                        flexShrink: 0,
                                                        '& .MuiInputBase-input[type="date"]': {
                                                            paddingRight: '8px',
                                                            paddingLeft: '14px',
                                                            '&::-webkit-calendar-picker-indicator': {
                                                                cursor: 'pointer',
                                                                padding: '2px',
                                                                marginLeft: '0px',
                                                                marginRight: '4px',
                                                                opacity: 0.7,
                                                                position: 'relative',
                                                                '&:hover': {
                                                                    opacity: 1,
                                                                },
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-root': {
                                                            paddingRight: '8px',
                                                        },
                                                    }}
                                                />
                                                <TextField
                                                    label="End Date"
                                                    type="date"
                                                    variant="outlined"
                                                    size="small"
                                                    value={endDate || ""}
                                                    onChange={(e) => setEndDate(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{
                                                        width: { xs: '140px', sm: '160px', md: '180px' },
                                                        flexShrink: 0,
                                                        '& .MuiInputBase-input[type="date"]': {
                                                            paddingRight: '8px',
                                                            paddingLeft: '14px',
                                                            '&::-webkit-calendar-picker-indicator': {
                                                                cursor: 'pointer',
                                                                padding: '2px',
                                                                marginLeft: '0px',
                                                                marginRight: '4px',
                                                                opacity: 0.7,
                                                                position: 'relative',
                                                                '&:hover': {
                                                                    opacity: 1,
                                                                },
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-root': {
                                                            paddingRight: '8px',
                                                        },
                                                    }}
                                                />
                                                <Button
                                                    variant="contained"
                                                    onClick={handleApplyFilters}
                                                    disabled={listLoading || isApplyingFilters || (!searchQuery.trim() && !startDate && !endDate)}
                                                    sx={{
                                                        borderRadius: "8px",
                                                        textTransform: "none",
                                                        backgroundColor: "var(--primary-color, #1976d2)",
                                                        color: "#fff",
                                                        height: 40,
                                                        px: 2,
                                                        flexShrink: 0,
                                                        "&:hover": { backgroundColor: "var(--primary-color, #1976d2)", opacity: 0.9 },
                                                        "&:disabled": {
                                                            backgroundColor: "rgba(0, 0, 0, 0.12)",
                                                            color: "rgba(0, 0, 0, 0.26)",
                                                        },
                                                    }}
                                                >
                                                    {isApplyingFilters ? "Applying..." : "Apply Filters"}
                                                </Button>
                                            </Box>
                                            {/* Bottom Line: All Action Buttons */}
                                            <div className="modern-toolbar d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                                                {/* Inactive / Show All Users */}
                                                <Button
                                                    className="modern-toolbar-btn"
                                                    variant="contained"
                                                    size="small"
                                                    onClick={showInactive ? handleShowAllUsers : handleManageSelected}
                                                >
                                                    {showInactive ? "Show All Users" : "Inactive Users"}
                                                </Button>
                                                {/* Send Gift */}
                                                <Button
                                                    className="modern-toolbar-btn"
                                                    variant="contained"
                                                    color="primary"
                                                    size="small"
                                                    onClick={handleCouponClick}
                                                    disabled={selectedRows.length === 0}
                                                >
                                                    Send Gift Amount
                                                </Button>
                                                {/* Success Recharge */}
                                                {selectedRows.length === 1 && (
                                                    <Button
                                                        className="modern-toolbar-btn"
                                                        variant="contained"
                                                        color="success"
                                                        size="small"
                                                        onClick={handleRechargeClick}
                                                    >
                                                        Success Recharge
                                                    </Button>
                                                )}
                                                {/* Refund */}
                                                {selectedRows.length === 1 && (
                                                    <Button
                                                        className="modern-toolbar-btn"
                                                        variant="contained"
                                                        color="warning"
                                                        size="small"
                                                        onClick={handleRefundClick}
                                                    >
                                                        Refund
                                                    </Button>
                                                )}
                                                {/* Export */}
                                                <Button
                                                    className="modern-toolbar-btn"
                                                    variant="contained"
                                                    color="primary"
                                                    size="small"
                                                    startIcon={<BsDownload />}
                                                    onClick={handleExport}
                                                    disabled={isExporting}
                                                >
                                                    {isExporting ? "Exporting..." : "Export"}
                                                </Button>
                                                {/* Upload CSV */}
                                                <Button
                                                    className="modern-toolbar-btn"
                                                    variant="contained"
                                                    color="secondary"
                                                    size="small"
                                                    onClick={handleDownloadCSV}
                                                    disabled={isDownloadingCSV}
                                                >
                                                    {isDownloadingCSV ? "Downloading..." : "Download CSV"}
                                                </Button>
                                                {/* Reset */}
                                                <Button
                                                    variant="outlined"
                                                    onClick={handleResetFilter}
                                                    sx={{
                                                        borderRadius: '8px',
                                                        textTransform: 'none',
                                                        borderColor: 'var(--primary-color, #1976d2)',
                                                        color: 'var(--primary-color, #1976d2)',
                                                        height: 40,
                                                        px: 2,
                                                        '&:hover': { borderColor: 'var(--primary-color, #1976d2)', backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    Reset
                                                </Button>

                                                {/* Upload CSV for Gift Amount */}
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    onClick={handleBulkUploadGiftAmountClick}
                                                    disabled={isBulkUploading || isBulkFileUploaded}
                                                    sx={{
                                                        borderRadius: '8px',
                                                        textTransform: 'none',
                                                        height: 40,
                                                        px: 2,
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    {isBulkUploading ? "Uploading..." : "Upload CSV for Gift Amount"}
                                                </Button>

                                                {/* Execute Bulk GiftAmount */}
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    onClick={handleExecuteBulkGiftAmount}
                                                    disabled={isBulkExecuting || !isBulkFileUploaded}
                                                    sx={{
                                                        borderRadius: '8px',
                                                        textTransform: 'none',
                                                        height: 40,
                                                        px: 2,
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    {isBulkExecuting ? "Executing..." : "Execute Bulk GiftAmount"}
                                                </Button>

                                                {/* Sample CSV for Gift Amount */}
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    size="small"
                                                    onClick={handleDownloadSampleGiftAmountCSV}
                                                    sx={{
                                                        borderRadius: '8px',
                                                        textTransform: 'none',
                                                        height: 40,
                                                        px: 2,
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    Sample CSV for Gift Amount
                                                </Button>
                                            </div>
                                            <input
                                                type="file"
                                                accept=".csv,.xlsx"
                                                ref={bulkUploadInputRef}
                                                style={{ display: 'none' }}
                                                onChange={handleBulkUploadFileChange}
                                            />
                                        </Box>

                                        <DataGrid
                                            rows={currentGridRows}
                                            columns={columns}
                                            getRowId={(row) => row.id}
                                            autoHeight
                                            loading={showInactive ? inactiveUsersLoading : loading}
                                            checkboxSelection
                                            rowSelectionModel={selectedRows}
                                            onRowSelectionModelChange={(newSelectionModel) => setSelectedRows(newSelectionModel)}
                                            keepNonExistentRowsSelected
                                            paginationMode="server"
                                            hideFooterPagination
                                            columnVisibilityModel={columnVisibilityModel}
                                            onColumnVisibilityModelChange={setColumnVisibilityModel}
                                            disableRowSelectionOnClick
                                            sx={{
                                                borderRadius: '12px',
                                                borderColor: 'var(--border-color)',
                                                '& .MuiDataGrid-columnHeaders': {
                                                    backgroundColor: 'var(--color-200)',
                                                    borderBottom: '1px solid var(--border-color)'
                                                },
                                                '& .MuiDataGrid-row': { minHeight: 44 },
                                                '& .MuiDataGrid-cell': { borderColor: 'var(--border-color)', fontSize: '13px' },
                                                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, fontSize: '12px' },
                                                '& .MuiDataGrid-footerContainer': {
                                                    display: 'none',
                                                },
                                            }}
                                        />
                                        <TablePagination
                                            component="div"
                                            count={showInactive ? inactiveRowCount : (paginationInfo?.totalDocs || 0)}
                                            page={paginationModel.page}
                                            onPageChange={handleChangePage}
                                            rowsPerPage={paginationModel.pageSize}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                            rowsPerPageOptions={[10, 25, 50]}
                                            labelRowsPerPage="Rows per page:"
                                            sx={{
                                                borderTop: '1px solid var(--border-color)',
                                                '& .MuiTablePagination-toolbar': {
                                                    padding: '12px 20px',
                                                },
                                            }}
                                        />
                                    </Box>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Dialog open={couponOpen} onClose={handleCouponClose} fullWidth maxWidth="sm" >
                    <DialogTitle>Send Coupon</DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ mb: 2 }}>
                            Enter coupon details for {selectedRows.length} selected user(s).
                        </DialogContentText>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Amount (₹)"
                            type="number"
                            fullWidth
                            variant="outlined"
                            value={couponValue}
                            onChange={(e) => setCouponValue(e.target.value)}
                            error={
                                submitAttemptedCoupon &&
                                (!couponValue.trim() ||
                                    Number(couponValue) < 1 ||
                                    Number(couponValue) > 9999999)
                            }
                            helperText={
                                submitAttemptedCoupon && !couponValue.trim()
                                    ? "Amount is required"
                                    : submitAttemptedCoupon && Number(couponValue) < 1
                                        ? "Minimum amount should be ₹1"
                                        : submitAttemptedCoupon && Number(couponValue) > 9999999
                                            ? "Amount cannot exceed 7 digits (₹9,999,999)"
                                            : ""
                            }
                            inputProps={{
                                min: 1,
                                max: 9999999,
                            }}
                        />
                        <TextField margin="dense" label="Subject" fullWidth variant="outlined" value={couponSubject}
                            onChange={(e) => setCouponSubject(e.target.value)} error={submitAttemptedCoupon && !couponSubject.trim()}
                            helperText={submitAttemptedCoupon && !couponSubject.trim() ? 'Subject is required' : ''} />
                        <TextField margin="dense" label="Message" fullWidth multiline rows={4} variant="outlined" value={couponMessage}
                            onChange={(e) => setCouponMessage(e.target.value)} error={submitAttemptedCoupon && !couponMessage.trim()}
                            helperText={submitAttemptedCoupon && !couponMessage.trim() ? 'Message is required' : ''} />
                    </DialogContent>
                    <DialogActions sx={{ p: '16px 24px' }}>
                        <Button onClick={handleCouponClose}>Cancel</Button>
                        {isSend ?
                            <Button variant="contained" color="primary" disabled>Sending...</Button>
                            : <Button onClick={handleCouponSend} variant="contained" color="primary">Send</Button>
                        }
                    </DialogActions>
                </Dialog>

                <Dialog open={rechargeOpen} onClose={handleRechargeClose} fullWidth maxWidth="sm" >
                    <DialogTitle>Success Recharge</DialogTitle>
                    <DialogContent>
                        {selectedRows.length === 1 && baseDataSet && (
                            <>
                                <DialogContentText sx={{ mb: 2 }}>
                                    {(() => {
                                        const selectedUser = baseDataSet.find(user => user._id === selectedRows[0]);
                                        return selectedUser ? `User: ${selectedUser.fullName || 'N/A'} | Mobile: ${selectedUser.mobileNumber || 'N/A'}` : 'User not found';
                                    })()}
                                </DialogContentText>
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    label="Amount (₹)"
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    value={rechargeValue}
                                    onChange={(e) => setRechargeValue(e.target.value)}
                                    error={
                                        submitAttemptedRecharge &&
                                        (!rechargeValue.trim() ||
                                            Number(rechargeValue) < 1 ||
                                            Number(rechargeValue) > 9999999)
                                    }
                                    helperText={
                                        submitAttemptedRecharge && !rechargeValue.trim()
                                            ? "Amount is required"
                                            : submitAttemptedRecharge && Number(rechargeValue) < 1
                                                ? "Minimum amount should be ₹1"
                                                : submitAttemptedRecharge && Number(rechargeValue) > 9999999
                                                    ? "Amount cannot exceed 7 digits (₹9,999,999)"
                                                    : ""
                                    }
                                    inputProps={{
                                        min: 1,
                                        max: 9999999,
                                    }}
                                />
                            </>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: '16px 24px' }}>
                        <Button onClick={handleRechargeClose}>Cancel</Button>
                        {isSendRecharge ?
                            <Button variant="contained" color="success" disabled>Processing...</Button>
                            : <Button onClick={handleRechargeSend} variant="contained" color="success">Process Recharge</Button>
                        }
                    </DialogActions>
                </Dialog>

                <Dialog open={refundOpen} onClose={handleRefundClose} fullWidth maxWidth="sm" >
                    <DialogTitle>Process Refund</DialogTitle>
                    <DialogContent>
                        {selectedRows.length === 1 && baseDataSet && (
                            <>
                                <DialogContentText sx={{ mb: 2 }}>
                                    {(() => {
                                        const selectedUser = baseDataSet.find(user => user._id === selectedRows[0]);
                                        return selectedUser ? `User: ${selectedUser.fullName || 'N/A'} | Mobile: ${selectedUser.mobileNumber || 'N/A'}` : 'User not found';
                                    })()}
                                </DialogContentText>

                                {/* Transactions Selection */}
                                {refundTransactionsLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : refundTransactions.length > 0 ? (
                                    <Box sx={{ mb: 2.5 }}>
                                        <DialogContentText sx={{ mb: 1.5, fontWeight: 'bold', fontSize: '14px' }}>
                                            Select Transaction (or enter manually):
                                        </DialogContentText>
                                        <Box
                                            onScroll={handleRefundScroll}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1,
                                                maxHeight: '240px',
                                                overflowY: 'auto',
                                                p: 1.5,
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '8px',
                                                backgroundColor: 'var(--card-color)',
                                                '&::-webkit-scrollbar': {
                                                    width: '6px',
                                                },
                                                '&::-webkit-scrollbar-track': {
                                                    backgroundColor: 'transparent',
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    backgroundColor: 'rgba(0,0,0,0.2)',
                                                    borderRadius: '3px',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0,0,0,0.3)',
                                                    },
                                                },
                                            }}
                                        >
                                            {refundTransactions.map((transaction, index) => (
                                                <Box
                                                    key={transaction._id || `${transaction.orderId || transaction.transactionId || 'tx'}-${index}`}
                                                    onClick={() => handleTransactionSelect(transaction)}
                                                    sx={{
                                                        p: 1.25,
                                                        py: 1.5,
                                                        border: selectedTransaction?._id === transaction._id
                                                            ? '2px solid var(--primary-color)'
                                                            : '1px solid var(--border-color)',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        backgroundColor: selectedTransaction?._id === transaction._id
                                                            ? 'rgba(25, 118, 210, 0.08)'
                                                            : 'transparent',
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            borderColor: 'var(--primary-color)',
                                                            backgroundColor: selectedTransaction?._id === transaction._id
                                                                ? 'rgba(25, 118, 210, 0.12)'
                                                                : 'rgba(25, 118, 210, 0.04)',
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                mb: 0.5
                                                            }}>
                                                                <Box sx={{
                                                                    fontWeight: '600',
                                                                    fontSize: '13px',
                                                                    color: 'text.primary',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    Order ID: {transaction.orderId || '-'}
                                                                </Box>
                                                                <Box sx={{
                                                                    fontSize: '10px',
                                                                    px: 0.75,
                                                                    py: 0.25,
                                                                    borderRadius: '4px',
                                                                    color: transaction.type === 'credit' ? '#2e7d32' : '#d32f2f',
                                                                    backgroundColor: transaction.type === 'credit'
                                                                        ? 'rgba(46, 125, 50, 0.1)'
                                                                        : 'rgba(211, 47, 47, 0.1)',
                                                                    fontWeight: '600',
                                                                    textTransform: 'uppercase',
                                                                    flexShrink: 0
                                                                }}>
                                                                    {transaction.type || '-'}
                                                                </Box>
                                                            </Box>
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1.5,
                                                                flexWrap: 'wrap',
                                                                mb: 0.5
                                                            }}>
                                                                <Box sx={{
                                                                    fontSize: '12px',
                                                                    color: 'text.secondary',
                                                                    fontWeight: '500'
                                                                }}>
                                                                    Amount: ₹{transaction.amount ? Math.abs(transaction.amount) : 0}
                                                                </Box>
                                                                {transaction.date && (
                                                                    <Box sx={{
                                                                        fontSize: '11px',
                                                                        color: 'text.secondary'
                                                                    }}>
                                                                        Date: {new Date(transaction.date).toLocaleString('en-IN', {
                                                                            day: '2-digit',
                                                                            month: '2-digit',
                                                                            year: 'numeric',
                                                                            hour: '2-digit',
                                                                            minute: '2-digit'
                                                                        })}
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                            {transaction.description && (
                                                                <Box sx={{
                                                                    fontSize: '11px',
                                                                    color: 'text.secondary',
                                                                    mt: 0.5,
                                                                    lineHeight: 1.4,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical',
                                                                }}>
                                                                    Description: {transaction.description}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            ))}
                                            {refundLoadingMore && (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                                                    <CircularProgress size={24} />
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'var(--color-200)', borderRadius: 1 }}>
                                        <DialogContentText sx={{ fontSize: '13px', m: 0 }}>
                                            No transactions found. Please enter details manually.
                                        </DialogContentText>
                                    </Box>
                                )}

                                {/* Refund Form Fields */}
                                <TextField
                                    autoFocus={!refundTransactions.length}
                                    margin="dense"
                                    label="Order ID"
                                    fullWidth
                                    variant="outlined"
                                    value={refundOrderId}
                                    onChange={(e) => setRefundOrderId(e.target.value)}
                                    error={submitAttemptedRefund && !refundOrderId.trim()}
                                    helperText={submitAttemptedRefund && !refundOrderId.trim() ? "Order ID is required" : ""}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    margin="dense"
                                    label="Refund Amount (₹)"
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    value={refundAmount}
                                    // onChange={(e) => setRefundAmount(e.target.value)}
                                    disabled
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                    error={
                                        submitAttemptedRefund &&
                                        (!refundAmount.trim() ||
                                            Number(refundAmount) < 1 ||
                                            Number(refundAmount) > 9999999 ||
                                            (selectedTransaction?.amount && Number(refundAmount) > Math.abs(selectedTransaction.amount)))
                                    }
                                    helperText={
                                        submitAttemptedRefund && !refundAmount.trim()
                                            ? "Refund amount is required"
                                            : submitAttemptedRefund && Number(refundAmount) < 1
                                                ? "Minimum amount should be ₹1"
                                                : submitAttemptedRefund && Number(refundAmount) > 9999999
                                                    ? "Amount cannot exceed 7 digits (₹9,999,999)"
                                                    : submitAttemptedRefund && selectedTransaction?.amount && Number(refundAmount) > Math.abs(selectedTransaction.amount)
                                                        ? `Refund amount cannot exceed transaction amount (₹${Math.abs(selectedTransaction.amount)})`
                                                        : ""
                                    }
                                    inputProps={{
                                        min: 1,
                                        max: selectedTransaction?.amount ? Math.abs(selectedTransaction.amount) : 9999999,
                                        readOnly: true,
                                    }}
                                    sx={{
                                        '& .MuiInputBase-input.Mui-disabled': {
                                            WebkitTextFillColor: 'currentColor',
                                            cursor: 'not-allowed',
                                        },
                                        '& .MuiInputBase-root.Mui-disabled': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                            cursor: 'not-allowed',
                                        },
                                    }}
                                />
                            </>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: '16px 24px' }}>
                        <Button onClick={handleRefundClose}>Cancel</Button>
                        {isProcessingRefund ?
                            <Button variant="contained" color="warning" disabled>Processing...</Button>
                            : <Button onClick={handleRefundSubmit} variant="contained" color="warning">Process Refund</Button>
                        }
                    </DialogActions>
                </Dialog>

                <Dialog open={openDeleteDialog} onClose={handleDeleteDialogClose}>
                    <DialogTitle>{"Delete Confirmation"}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete this user record? This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeleteDialogClose}>Cancel</Button>
                        <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>Confirm Delete</Button>
                    </DialogActions>
                </Dialog>

                {/* Wallet Balance Dialog */}
                <Dialog
                    open={walletBalanceDialogOpen}
                    onClose={handleWalletBalanceClose}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>Wallet Balance Report</DialogTitle>
                    <DialogContent>
                        {walletBalanceLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                            </Box>
                        ) : !walletBalanceData ? (
                            <Box sx={{ textAlign: 'center', p: 3 }}>
                                No wallet balance data found
                            </Box>
                        ) : (
                            <Box>
                                {/* Wallet Info Section */}
                                <Box sx={{ mb: 3, p: 2, backgroundColor: 'var(--color-200)', borderRadius: 1 }}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                        <Box>
                                            <strong>Username:</strong> {walletBalanceData.username || '-'}
                                        </Box>
                                        <Box>
                                            <strong>Mobile Number:</strong> {walletBalanceData.mobileNumber || '-'}
                                        </Box>
                                        <Box>
                                            <strong>Total Balance:</strong> ₹{walletBalanceData.totalBalance || 0}
                                        </Box>
                                        <Box>
                                            <strong>Actual Balance:</strong> ₹{walletBalanceData.actualBalance || 0}
                                        </Box>
                                        <Box>
                                            <strong>Gift Amount:</strong> ₹{walletBalanceData.giftAmount || 0}
                                        </Box>
                                        <Box>
                                            <strong>Last Updated:</strong> {walletBalanceData.updatedAt ? new Date(walletBalanceData.updatedAt).toLocaleString('en-IN', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            }) : '-'}
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Transactions Table */}
                                <Box sx={{ mt: 2 }}>
                                    <Box sx={{ mb: 1, fontWeight: 'bold' }}>Transactions:</Box>
                                    {walletTransactions.length === 0 ? (
                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                            No transactions found
                                        </Box>
                                    ) : (
                                        <TableContainer component={Paper} sx={{ maxHeight: 400, overflowY: 'auto' }} onScroll={handleWalletScroll}>
                                            <Table stickyHeader size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'var(--color-200)' }}>S.No</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'var(--color-200)' }}>Transaction ID</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'var(--color-200)' }}>Type</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'var(--color-200)' }}>Amount</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'var(--color-200)' }}>Order ID</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'var(--color-200)' }}>Description</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'var(--color-200)' }}>Status</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'var(--color-200)' }}>Date</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {walletTransactions.map((transaction, index) => (
                                                        <TableRow key={transaction._id || `${transaction.transactionId || transaction.orderId || 'tx'}-${index}`}>
                                                            <TableCell>{index + 1}</TableCell>
                                                            <TableCell>{transaction.transactionId || '-'}</TableCell>
                                                            <TableCell>
                                                                <Box sx={{
                                                                    color: transaction.type === 'credit' ? 'green' : 'red',
                                                                    fontWeight: 'bold'
                                                                }}>
                                                                    {transaction.type?.toUpperCase() || '-'}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>₹{transaction.amount || 0}</TableCell>
                                                            <TableCell>{transaction.orderId || '-'}</TableCell>
                                                            <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {transaction.description || '-'}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box sx={{
                                                                    color: transaction.status === 'success' ? 'green' : 'red',
                                                                    fontWeight: 'bold'
                                                                }}>
                                                                    {transaction.status?.toUpperCase() || '-'}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                {transaction.date ? new Date(transaction.date).toLocaleString('en-IN', {
                                                                    day: '2-digit',
                                                                    month: '2-digit',
                                                                    year: 'numeric',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                }) : '-'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                    {walletLoadingMore && (
                                                        <TableRow>
                                                            <TableCell colSpan={8} align="center" sx={{ py: 1.5 }}>
                                                                <CircularProgress size={24} />
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleWalletBalanceClose}>Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Bulk Upload Confirm Modal */}
                <Dialog open={bulkUploadModalOpen} onClose={handleCancelBulkUpload}>
                    <DialogTitle>Confirm Bulk Upload</DialogTitle>
                    <DialogContent>
                        <Box sx={{ p: 2 }}>
                            <p>Are you sure you want to upload this file?</p>
                            <p><strong>{selectedBulkUploadFile?.name}</strong></p>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelBulkUpload} disabled={isBulkUploading}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmBulkUpload}
                            color="primary"
                            variant="contained"
                            disabled={isBulkUploading}
                        >
                            {isBulkUploading ? "Uploading..." : "Upload"}
                        </Button>
                    </DialogActions>
                </Dialog>

            </div>
        </>
    );
}

export default UsersList;